<template>
    <div class="min-h-screen">
        <slot v-if="currentUser.id && !isAuthenticating" />
        <div class="pointer-events-none absolute inset-0 z-10 flex h-screen items-center justify-center">
            <div
                :class="{
                    'opacity-0! transition delay-200 duration-700': currentUser.id && !isAuthenticating,
                }"
                class="text-brand-purple flex h-48 w-48 items-center justify-center opacity-60"
            >
                <SerifLogoMark class="scale-up-center" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useCurrentUserStore } from '@/stores/useCurrentUserStore'
import { onMounted } from 'vue'

import SerifLogoMark from '@/assets/svg/serif-logo-mark.svg'
import { usePylon } from '@/composables/pylon/usePylon'
import { useSignInReturnPath } from '@/composables/useSignInReturnPath'

const isAuthenticating = ref(true)
const router = useRouter()
const currentUser = useCurrentUserStore()
const { init } = usePylon()
const returnPath = useSignInReturnPath()

async function authenticate() {
    isAuthenticating.value = true
    // Sign-in and OTP confirmation send the visitor back to the page they were opening.
    const remember = () => returnPath.remember(router.currentRoute.value.fullPath)

    // Only redirect to OTP confirmation if there's a valid pending OTP session
    // AND no stored access token at all (meaning the user never completed sign-in).
    // Do NOT redirect if tokens are merely expired — authenticate() will silently
    // refresh them.
    if (
        currentUser.cognito.pendingOtp &&
        currentUser.cognito.isPendingOtpValid &&
        !currentUser.cognito.tokens.accessToken
    ) {
        remember()
        return router.push(
            `/user-confirmation?${new URLSearchParams({
                email: currentUser.cognito.email,
            })}`
        )
    }

    currentUser
        .authenticate()
        .then(async () => {
            await currentUser.fetchAccountDetails()
            init(currentUser)
        })
        .catch(async (err) => {
            remember()
            // Forward to Confirmation Screen if user record is not confirmed
            if (err.message === 'User is not confirmed.') {
                if (currentUser.email) {
                    await currentUser.signInPasswordless({
                        email: currentUser.email,
                        preferredChallenge: 'EMAIL_OTP',
                    })
                    return router.push(
                        `/user-confirmation?${new URLSearchParams({
                            email: currentUser.email,
                        })}`
                    )
                }

                return router.push('/no-access')
            } else if (err.message?.startsWith('Forbidden')) {
                return router.push('/no-access')
            }

            return router.push('/sign-in', {
                query: {
                    error: err.message,
                },
            })
        })
        .finally(() => {
            isAuthenticating.value = false
        })
}

onMounted(async () => {
    await authenticate()
})
</script>

<style lang="css" scoped>
.scale-up-center {
    -webkit-animation: scale-up-center 0.7s cubic-bezier(0.39, 0.575, 0.565, 1) both;
    animation: scale-up-center 0.7s cubic-bezier(0.39, 0.575, 0.565, 1) both;
    transform-origin: center;
}

/* ----------------------------------------------
 * Generated by Animista on 2024-12-27 13:11:2
 * Licensed under FreeBSD License.
 * See http://animista.net/license for more info. 
 * w: http://animista.net, t: @cssanimista
 * ---------------------------------------------- */

/**
 * ----------------------------------------
 * animation scale-up-center
 * ----------------------------------------
 */
@-webkit-keyframes scale-up-center {
    0% {
        -webkit-transform: scale(0.5) rotate(-15deg);
        transform: scale(0.5) rotate(-15deg);
    }
    100% {
        -webkit-transform: scale(1) rotate(0deg);
        transform: scale(1) rotate(0deg);
    }
}
@keyframes scale-up-center {
    0% {
        -webkit-transform: scale(0.5) rotate(-15deg);
        transform: scale(0.5) rotate(-15deg);
    }
    100% {
        -webkit-transform: scale(1) rotate(0deg);
        transform: scale(1) rotate(0deg);
    }
}
</style>
